import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { tenantAPI, authAPI } from "@/lib/api";
import { Building2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { invalidatePaymentMethods } from "@/lib/paymentMethods";

export default function TenantSelectPage({ user, setUser }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    tenantAPI.list()
      .then(r => { setTenants(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const selectTenant = async (tenantId) => {
    try {
      await tenantAPI.select(tenantId);
      invalidatePaymentMethods();
      const res = await authAPI.me();
      setUser(res.data);
      toast.success("Espace selectionne");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="tenant-select-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <img src="/logo_komptai_light.svg" alt="KomptAI" className="h-10 mx-auto" />
          </h1>
          <p className="text-slate-500 mt-2">Sélectionnez votre espace de travail</p>
        </div>
        <div className="space-y-3">
          {tenants.map(t => (
            <Card
              key={t.id}
              className="p-4 cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all"
              onClick={() => selectTenant(t.id)}
              data-testid={`tenant-card-${t.slug}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Building2 size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role === "owner" ? "Propriétaire" : t.role === "admin" ? "Administrateur" : "Utilisateur"}</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-slate-400" />
              </div>
            </Card>
          ))}
          {tenants.length === 0 && (
            <Card className="p-8 text-center">
              <p className="text-slate-500">Aucun espace disponible. Contactez votre administrateur.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
